import { useEffect, useRef, useState, useCallback, forwardRef } from "react";
import { toast } from "sonner";
import {
  Plus, Save, Trash2, Eye, EyeOff, RotateCcw, Settings2,
  Megaphone, School, BookOpen, Award, Calendar, Star, Bell,
  Rocket, Globe, Zap, Users, AlertCircle, CheckCircle, Info,
  Heart, Trophy, GraduationCap, Clipboard, FileText, Building, X,
  Copy, ChevronUp, ChevronDown, Monitor, Tablet, Smartphone, Pin,
} from "lucide-react";
import api from "@/lib/api";
import { Header } from "./Schools";
import { AdminModal } from "@/components/AdminModal";

const ICON_MAP = {
  Megaphone, School, BookOpen, Award, Calendar, Star, Bell,
  Rocket, Globe, Zap, Users, AlertCircle, CheckCircle, Info,
  Heart, Trophy, GraduationCap, Clipboard, FileText, Building,
};
const ICON_NAMES = Object.keys(ICON_MAP);

const NEWS_TYPES = [
  "general","announcement","workshop","training","booking",
  "certificate","admission","event","holiday","notice",
  "achievement","emergency","custom",
];

const COLORS = [
  "#4F46E5","#0F172A","#16A34A","#DC2626","#D97706",
  "#0891B2","#7C3AED","#DB2777","#65A30D","#EA580C",
];

const FONT_FAMILIES = [
  { label: "Default (inherit)", value: "" },
  { label: "Inter", value: "Inter, sans-serif" },
  { label: "Poppins", value: "Poppins, sans-serif" },
  { label: "Roboto", value: "Roboto, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Montserrat", value: "Montserrat, sans-serif" },
  { label: "Open Sans", value: "'Open Sans', sans-serif" },
];

const DEFAULT_SETTINGS = {
  enabled: true, height: 40, bg_color: "#0F172A", bg_gradient: "",
  glassmorphism: false, glass_opacity: 0.15, glass_blur: 8,
  border_radius: 0, border: "", border_color: "", shadow: "",
  font_family: "", font_size: 14, font_weight: 500, font_style: "normal",
  font_color: "#FFFFFF", hover_color: "", letter_spacing: 0,
  scroll_speed: 40, pause_on_hover: true, gap: 60, padding_x: 16,
  separator: "•", separator_icon: "", auto_repeat: true, direction: "ltr",
  rounded: false, show_close_btn: false, animation: "scroll",
  animation_easing: "linear", sticky: true, ticker_opacity: 1,
  desktop_visible: true, tablet_visible: true, mobile_visible: true,
  show_booking_news: true, show_admin_news: true,
};

const BLANK_NEWS = {
  headline: "", description: "", news_type: "general", icon: "Megaphone",
  color: "#4F46E5", theme: "default", priority: 0, pinned: false,
  visible: true, link: "", link_new_tab: false, display_order: 0,
  start_date: "", expiry_date: "",
};

const SI = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400 transition bg-white";

function Card({ title, children, action }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      {title && (
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">{title}</span>
          {action}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}

function Lbl({ children }) {
  return <label className="block text-xs font-semibold text-gray-700 mb-1">{children}</label>;
}
const Inp = forwardRef(function Inp(props, ref) {
  return <input {...props} ref={ref} className={SI} />;
});
function Sel({ children, ...props }) {
  return <select {...props} className={SI}>{children}</select>;
}
function Toggle({ label, desc, checked, onChange }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer py-2 select-none">
      <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
        className={`relative mt-0.5 shrink-0 w-9 h-5 rounded-full transition-colors ${checked ? "bg-indigo-600" : "bg-gray-300"}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-4" : ""}`} />
      </button>
      <span>
        <span className="block text-sm font-medium text-gray-800">{label}</span>
        {desc && <span className="block text-xs text-gray-500 mt-0.5">{desc}</span>}
      </span>
    </label>
  );
}
function ColorRow({ label, value, onChange }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <div className="flex gap-2">
        <input type="color" value={value || "#000000"} onChange={e => onChange(e.target.value)} className="w-10 h-10 rounded border border-gray-200 cursor-pointer shrink-0" />
        <Inp value={value} onChange={e => onChange(e.target.value)} placeholder="#000000 or css value" />
      </div>
    </div>
  );
}
function Row2({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>; }

/* ── Ticker Preview ── */
function TickerPreview({ settings, items }) {
  const trackRef = useRef(null);
  const animRef = useRef(null);
  const posRef = useRef(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    posRef.current = 0;
    const el = trackRef.current;
    if (!el || items.length === 0) return;
    const speed = Math.max(5, Number(settings.scroll_speed || 40));
    let last = null;
    function step(ts) {
      if (!last) last = ts;
      const dt = Math.min(ts - last, 100); last = ts;
      if (!paused) {
        posRef.current -= (speed * dt) / 1000;
        const half = el.scrollWidth / 2;
        if (Math.abs(posRef.current) >= half) posRef.current = 0;
        el.style.transform = `translateX(${posRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(step);
    }
    animRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animRef.current);
  }, [items, paused, settings.scroll_speed]);

  let bg = settings.bg_color || "#0F172A";
  if (settings.bg_gradient) bg = settings.bg_gradient;

  const wrapStyle = {
    height: `${Math.min(settings.height || 40, 80)}px`,
    background: settings.glassmorphism ? `rgba(255,255,255,${settings.glass_opacity || 0.15})` : bg,
    backdropFilter: settings.glassmorphism ? `blur(${settings.glass_blur || 8}px)` : undefined,
    fontSize: `${settings.font_size || 14}px`,
    fontFamily: settings.font_family || undefined,
    fontWeight: settings.font_weight || 500,
    color: settings.font_color || "#fff",
    letterSpacing: settings.letter_spacing ? `${settings.letter_spacing}px` : undefined,
    borderRadius: settings.rounded ? `${settings.border_radius || 8}px` : undefined,
    border: settings.border_color ? `1px solid ${settings.border_color}` : settings.border || undefined,
    boxShadow: settings.shadow || undefined,
    opacity: settings.ticker_opacity ?? 1,
    overflow: "hidden", display: "flex", alignItems: "center", width: "100%",
  };

  const doubled = [...items, ...items];
  const gap = Number(settings.gap || 60);
  const sep = settings.separator !== undefined ? settings.separator : "•";

  if (items.length === 0) return (
    <div style={{ ...wrapStyle, justifyContent: "center", opacity: 0.5 }}>
      <Bell style={{ width: 13, height: 13, marginRight: 6 }} />
      <span style={{ fontSize: 12 }}>No visible items</span>
    </div>
  );

  return (
    <div style={wrapStyle}>
      <div style={{ flex: 1, overflow: "hidden", height: "100%", display: "flex", alignItems: "center" }}>
        <div ref={trackRef} style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap", willChange: "transform" }}
          onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          {doubled.map((item, i) => {
            const Ico = ICON_MAP[item.icon];
            return (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", paddingRight: gap, paddingLeft: settings.padding_x || 0 }}>
                {sep && i > 0 && <span style={{ marginRight: 12, opacity: 0.5 }}>{sep}</span>}
                {Ico && <Ico style={{ width: 13, height: 13, color: item.color || "currentColor", flexShrink: 0 }} />}
                <span style={{ marginLeft: Ico ? 7 : 0 }}>{item.headline}</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Section divider inside the form ── */
function FormSection({ title, children }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-xs font-bold text-gray-500 uppercase tracking-widest whitespace-nowrap">{title}</span>
        <div className="flex-1 h-px bg-gray-100" />
      </div>
      {children}
    </div>
  );
}

/* ── News Form Modal — uses AdminModal portal, 900px wide, 2-col grid ── */
function NewsForm({ initial, onSave, onClose, isBooking }) {
  const [form, setForm] = useState({ ...BLANK_NEWS, ...initial });
  const [saving, setSaving] = useState(false);
  const headlineRef = useRef(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const Ico = ICON_MAP[form.icon];

  // Auto-focus headline on open
  useEffect(() => {
    const t = setTimeout(() => headlineRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  async function submit() {
    if (!form.headline.trim()) { toast.error("Headline is required"); headlineRef.current?.focus(); return; }
    setSaving(true);
    try {
      const { data } = initial?.id
        ? await api.put(`/ticker/news/${initial.id}`, form)
        : await api.post("/ticker/news", form);
      toast.success(initial?.id ? "Updated" : "Created");
      onSave(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  const title = (
    <span className="flex items-center gap-2">
      {initial?.id ? "Edit News Item" : "Add News Item"}
      {isBooking && (
        <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium normal-case tracking-normal">Auto Booking</span>
      )}
    </span>
  );

  const footer = (
    <>
      <button onClick={onClose} className="px-6 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition min-h-[44px]">
        Cancel
      </button>
      <button onClick={submit} disabled={saving}
        className="inline-flex items-center justify-center gap-2 bg-[#0F172A] text-white text-sm font-semibold px-8 py-2.5 rounded-xl hover:bg-[#1e293b] transition disabled:opacity-60 min-h-[44px]">
        <Save className="w-4 h-4" />{saving ? "Saving…" : "Save"}
      </button>
    </>
  );

  return (
    <AdminModal
      title={title}
      subtitle={isBooking ? "Edit headline, icon, color, priority, pin and expiry. Other fields are managed automatically." : "Fill in the details below. All fields except Headline are optional."}
      onClose={onClose}
      footer={footer}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-8">

        {/* ── 1. Basic Information ── */}
        <FormSection title="Basic Information">
          <div>
            <Lbl>Headline *</Lbl>
            <Inp ref={headlineRef} value={form.headline} onChange={e => set("headline", e.target.value)} placeholder="e.g. School admissions now open for 2025–26…" />
          </div>
          {!isBooking && (
            <div>
              <Lbl>Short Description</Lbl>
              <textarea
                value={form.description}
                onChange={e => set("description", e.target.value)}
                placeholder="Optional subtitle or extra context shown on hover"
                rows={2}
                className={SI + " resize-none"}
              />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {!isBooking && (
              <div>
                <Lbl>News Type</Lbl>
                <Sel value={form.news_type} onChange={e => set("news_type", e.target.value)}>
                  {NEWS_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </Sel>
              </div>
            )}
            <div>
              <Lbl>Priority <span className="font-normal text-gray-400">(higher = first)</span></Lbl>
              <Inp type="number" min="0" value={form.priority} onChange={e => set("priority", parseInt(e.target.value) || 0)} />
            </div>
            <div>
              <Lbl>Display Order</Lbl>
              <Inp type="number" min="0" value={form.display_order} onChange={e => set("display_order", parseInt(e.target.value) || 0)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
            <Toggle label="Visible" checked={!!form.visible} onChange={v => set("visible", v)} />
            <Toggle label="Pin to Top" checked={!!form.pinned} onChange={v => set("pinned", v)} />
            {!isBooking && (
              <Toggle label="Open Link in New Tab" checked={!!form.link_new_tab} onChange={v => set("link_new_tab", v)} />
            )}
          </div>
        </FormSection>

        {/* ── 2. Display ── */}
        <FormSection title="Display">
          <div>
            <Lbl>Icon</Lbl>
            <div className="flex flex-wrap gap-2 mt-1">
              {ICON_NAMES.map(name => {
                const I = ICON_MAP[name];
                return (
                  <button key={name} type="button" title={name} onClick={() => set("icon", name)}
                    className={`p-2.5 rounded-xl border-2 transition ${form.icon === name ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"}`}>
                    <I className="w-4 h-4 text-gray-600" />
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Lbl>Accent / Icon Color</Lbl>
            <div className="flex flex-wrap gap-2 mt-1 items-center">
              {COLORS.map(c => (
                <button key={c} type="button" onClick={() => set("color", c)}
                  title={c}
                  style={{ background: c }}
                  className={`w-8 h-8 rounded-full border-2 transition ${form.color === c ? "border-gray-800 scale-110" : "border-transparent hover:scale-105"}`} />
              ))}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="color" value={form.color} onChange={e => set("color", e.target.value)}
                  className="w-8 h-8 rounded-full border border-gray-200 cursor-pointer" />
                <span className="text-xs text-gray-500">Custom</span>
              </label>
              <span className="text-xs text-gray-400 font-mono">{form.color}</span>
            </div>
          </div>
          {/* Live preview strip */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Preview</div>
            <div className="px-4 py-3 flex items-center gap-2 text-sm bg-[#0F172A]" style={{ color: "#fff" }}>
              {Ico && <Ico style={{ width: 14, height: 14, color: form.color, flexShrink: 0 }} />}
              <span className="font-medium truncate">{form.headline || "Your headline will appear here"}</span>
              {form.description && <span style={{ opacity: 0.6, fontSize: 12 }}> — {form.description}</span>}
            </div>
          </div>
        </FormSection>

        {/* ── 3. Schedule ── */}
        <FormSection title="Schedule">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {!isBooking && (
              <div>
                <Lbl>Publish Date <span className="font-normal text-gray-400">(optional)</span></Lbl>
                <Inp type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)} />
              </div>
            )}
            <div>
              <Lbl>Expiry Date <span className="font-normal text-gray-400">(optional)</span></Lbl>
              <Inp type="date" value={form.expiry_date} onChange={e => set("expiry_date", e.target.value)} />
            </div>
          </div>
          {(form.start_date || form.expiry_date) && (
            <p className="text-xs text-gray-400">
              {form.start_date && `Publishes: ${form.start_date}`}
              {form.start_date && form.expiry_date && " · "}
              {form.expiry_date && `Expires: ${form.expiry_date}`}
            </p>
          )}
        </FormSection>

        {/* ── 4. Link — manual items only ── */}
        {!isBooking && (
          <FormSection title="Link">
            <div>
              <Lbl>URL <span className="font-normal text-gray-400">(optional)</span></Lbl>
              <Inp type="url" value={form.link} onChange={e => set("link", e.target.value)} placeholder="https://example.com/announcement" />
            </div>
            <Toggle label="Open in New Tab" checked={!!form.link_new_tab} onChange={v => set("link_new_tab", v)} />
          </FormSection>
        )}

      </div>
    </AdminModal>
  );
}

/* ── Main Page ── */
export default function NewsTickerAdmin() {
  const [news, setNews] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [modal, setModal] = useState(null);   // null = closed; {} = new; item = edit
  const [tab, setTab] = useState("news");

  async function load() {
    setLoading(true);
    try {
      const [newsRes, settingsRes] = await Promise.all([
        api.get("/ticker/news"),
        api.get("/ticker/settings"),
      ]);
      setNews(newsRes.data || []);
      setSettings({ ...DEFAULT_SETTINGS, ...(settingsRes.data || {}) });
    } catch { toast.error("Failed to load ticker data"); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function saveSettings() {
    setSavingSettings(true);
    try {
      await api.put("/ticker/settings", settings);
      toast.success("Ticker settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingSettings(false); }
  }

  async function toggleItem(item) {
    try {
      const { data } = await api.patch(`/ticker/news/${item.id}/toggle`);
      setNews(prev => prev.map(n => n.id === item.id ? { ...n, visible: data.visible } : n));
    } catch { toast.error("Toggle failed"); }
  }

  async function pinItem(item) {
    try {
      const { data } = await api.put(`/ticker/news/${item.id}`, { pinned: !item.pinned });
      setNews(prev => prev.map(n => n.id === item.id ? { ...n, pinned: data.pinned } : n));
      toast.success(item.pinned ? "Unpinned" : "Pinned");
    } catch { toast.error("Pin failed"); }
  }

  async function deleteItem(item) {
    if (!window.confirm("Delete this news item?")) return;
    try {
      await api.delete(`/ticker/news/${item.id}`);
      setNews(prev => prev.filter(n => n.id !== item.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  }

  async function duplicateItem(item) {
    try {
      const payload = {
        headline: `Copy of ${item.headline}`,
        description: item.description,
        news_type: item.news_type,
        icon: item.icon,
        color: item.color,
        priority: item.priority,
        pinned: false,
        visible: false,
        link: item.link,
        link_new_tab: item.link_new_tab,
        display_order: item.display_order,
        start_date: item.start_date,
        expiry_date: item.expiry_date,
      };
      const { data } = await api.post("/ticker/news", payload);
      setNews(prev => [data, ...prev]);
      toast.success("Duplicated");
    } catch { toast.error("Duplicate failed"); }
  }

  async function moveItem(item, direction) {
    const sorted = [...news].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const idx = sorted.findIndex(n => n.id === item.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const swapItem = sorted[swapIdx];
    const newOrder = item.display_order || 0;
    const swapOrder = swapItem.display_order || 0;
    try {
      await Promise.all([
        api.put(`/ticker/news/${item.id}`, { display_order: swapOrder }),
        api.put(`/ticker/news/${swapItem.id}`, { display_order: newOrder }),
      ]);
      setNews(prev => prev.map(n => {
        if (n.id === item.id) return { ...n, display_order: swapOrder };
        if (n.id === swapItem.id) return { ...n, display_order: newOrder };
        return n;
      }));
    } catch { toast.error("Reorder failed"); }
  }

  function onSaved(data) {
    setNews(prev => {
      const idx = prev.findIndex(n => n.id === data.id);
      if (idx >= 0) { const a = [...prev]; a[idx] = data; return a; }
      return [data, ...prev];
    });
    setModal(null);
  }

  const sset = (k, v) => setSettings(p => ({ ...p, [k]: v }));

  // Sort: pinned first, then by display_order
  const sortedNews = [...news].sort((a, b) => {
    if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
    return (a.display_order || 0) - (b.display_order || 0);
  });

  // Separate by source for the tab filter
  const bookingItems = sortedNews.filter(n => n.source === "booking");
  const manualItems = sortedNews.filter(n => n.source !== "booking");

  const previewItems = news.filter(n => n.visible);

  const TABS = [
    { id: "news", label: "News Items" },
    { id: "booking", label: `Booking News (${bookingItems.length})` },
    { id: "settings", label: "Ticker Settings" },
    { id: "preview", label: "Live Preview" },
  ];

  if (loading) {
    return (
      <div className="fade-up">
        <Header title="News Ticker" subtitle="Manage announcement bar and news feed." />
        <div className="mt-6 space-y-4 max-w-3xl">
          {[1, 2, 3].map(n => <div key={n} className="h-24 rounded-2xl bg-gray-100 animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up pb-12">
      {modal !== null && (
        <NewsForm
          initial={modal}
          onSave={onSaved}
          onClose={() => setModal(null)}
          isBooking={!!(modal?.source === "booking")}
        />
      )}

      <Header
        title="News Ticker"
        subtitle="Announcement bar above the website header. Auto booking news + admin announcements."
        actions={
          <div className="flex flex-wrap gap-2">
            {tab === "news" && (
              <button onClick={() => setModal({})}
                className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1e293b] transition">
                <Plus className="w-4 h-4" /> Add News
              </button>
            )}
            {tab === "settings" && (
              <button onClick={saveSettings} disabled={savingSettings}
                className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1e293b] transition disabled:opacity-60">
                <Save className="w-4 h-4" />{savingSettings ? "Saving…" : "Save Settings"}
              </button>
            )}
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex gap-0 mt-6 border-b border-gray-200 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition ${tab === t.id ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5 max-w-3xl space-y-5">

        {/* ── MANUAL NEWS ITEMS ── */}
        {tab === "news" && (
          <Card title={`Manual News (${manualItems.length})`}
            action={<button onClick={() => setModal({})} className="text-xs text-indigo-600 hover:underline flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Add</button>}>
            {manualItems.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">
                <Megaphone className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No manual news items yet. Add your first announcement.
              </div>
            ) : (
              <NewsItemList
                items={manualItems}
                allItems={sortedNews}
                onToggle={toggleItem}
                onPin={pinItem}
                onEdit={(item) => setModal(item)}
                onDuplicate={duplicateItem}
                onDelete={deleteItem}
                onMove={moveItem}
              />
            )}
          </Card>
        )}

        {/* ── BOOKING NEWS TAB ── */}
        {tab === "booking" && (
          <>
            <Card title="Auto-Booking News Control">
              <div className="divide-y divide-gray-100 -my-1">
                <Toggle
                  label="Show Booking News"
                  desc="Auto-generated announcements from approved bookings appear in the ticker."
                  checked={!!settings.show_booking_news}
                  onChange={async (v) => {
                    const newSettings = { ...settings, show_booking_news: v };
                    setSettings(newSettings);
                    try {
                      await api.put("/ticker/settings", newSettings);
                      toast.success(v ? "Booking news enabled" : "Booking news disabled");
                    } catch { toast.error("Failed to save"); }
                  }}
                />
                <Toggle
                  label="Show Manual News"
                  desc="Manually created announcements appear in the ticker."
                  checked={!!settings.show_admin_news}
                  onChange={async (v) => {
                    const newSettings = { ...settings, show_admin_news: v };
                    setSettings(newSettings);
                    try {
                      await api.put("/ticker/settings", newSettings);
                      toast.success(v ? "Manual news enabled" : "Manual news disabled");
                    } catch { toast.error("Failed to save"); }
                  }}
                />
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { label: "Manual Only", booking: false, admin: true },
                  { label: "Auto Only", booking: true, admin: false },
                  { label: "Both", booking: true, admin: true },
                ].map(({ label, booking, admin }) => {
                  const active = settings.show_booking_news === booking && settings.show_admin_news === admin;
                  return (
                    <button key={label} type="button"
                      onClick={async () => {
                        const newSettings = { ...settings, show_booking_news: booking, show_admin_news: admin };
                        setSettings(newSettings);
                        try {
                          await api.put("/ticker/settings", newSettings);
                          toast.success(`${label} mode enabled`);
                        } catch { toast.error("Failed to save"); }
                      }}
                      className={`px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition ${active ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </Card>

            <Card title={`Auto-Generated Booking Items (${bookingItems.length})`}>
              {bookingItems.length === 0 ? (
                <div className="text-center py-10 text-gray-400 text-sm">
                  <School className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No booking news yet. Items appear automatically when bookings are approved.
                </div>
              ) : (
                <NewsItemList
                  items={bookingItems}
                  allItems={sortedNews}
                  onToggle={toggleItem}
                  onPin={pinItem}
                  onEdit={(item) => setModal(item)}
                  onDuplicate={duplicateItem}
                  onDelete={deleteItem}
                  onMove={moveItem}
                  isBookingTab
                />
              )}
            </Card>
          </>
        )}

        {/* ── SETTINGS ── */}
        {tab === "settings" && (<>
          <Card title="Content Source">
            <div className="divide-y divide-gray-100 -my-1">
              <Toggle label="Enable Ticker" desc="Show announcement bar on the public website." checked={!!settings.enabled} onChange={v => sset("enabled", v)} />
              <Toggle label="Sticky" desc="Stays fixed at top while scrolling." checked={!!settings.sticky} onChange={v => sset("sticky", v)} />
              <Toggle label="Show Booking News" desc="Auto-generated news from approved bookings." checked={!!settings.show_booking_news} onChange={v => sset("show_booking_news", v)} />
              <Toggle label="Show Admin News" desc="Manually created announcements." checked={!!settings.show_admin_news} onChange={v => sset("show_admin_news", v)} />
              <Toggle label="Pause on Hover" checked={!!settings.pause_on_hover} onChange={v => sset("pause_on_hover", v)} />
              <Toggle label="Auto Repeat / Infinite Loop" checked={!!settings.auto_repeat} onChange={v => sset("auto_repeat", v)} />
              <Toggle label="Show Close Button" checked={!!settings.show_close_btn} onChange={v => sset("show_close_btn", v)} />
            </div>
          </Card>

          <Card title="Device Visibility">
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: "desktop_visible", label: "Desktop", Icon: Monitor },
                { key: "tablet_visible", label: "Tablet", Icon: Tablet },
                { key: "mobile_visible", label: "Mobile", Icon: Smartphone },
              ].map(({ key, label, Icon }) => (
                <button key={key} type="button" onClick={() => sset(key, !settings[key])}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition ${settings[key] ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-200 bg-white text-gray-400"}`}>
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-semibold">{label}</span>
                  <span className="text-[10px]">{settings[key] ? "Visible" : "Hidden"}</span>
                </button>
              ))}
            </div>
          </Card>

          <Card title="Appearance">
            <div className="space-y-4">
              <Row2>
                <div>
                  <Lbl>Height (px)</Lbl>
                  <Inp type="number" value={settings.height} onChange={e => sset("height", parseInt(e.target.value) || 40)} />
                </div>
                <div>
                  <Lbl>Ticker Opacity (0–1)</Lbl>
                  <Inp type="number" step="0.05" min="0" max="1" value={settings.ticker_opacity ?? 1} onChange={e => sset("ticker_opacity", parseFloat(e.target.value) ?? 1)} />
                </div>
                <div>
                  <Lbl>Border Radius (px)</Lbl>
                  <Inp type="number" value={settings.border_radius} onChange={e => sset("border_radius", parseInt(e.target.value) || 0)} />
                </div>
                <div>
                  <Lbl>Horizontal Item Padding (px)</Lbl>
                  <Inp type="number" value={settings.padding_x || 16} onChange={e => sset("padding_x", parseInt(e.target.value) || 0)} />
                </div>
              </Row2>
              <Row2>
                <ColorRow label="Background Color" value={settings.bg_color} onChange={v => sset("bg_color", v)} />
                <ColorRow label="Font Color" value={settings.font_color} onChange={v => sset("font_color", v)} />
                <ColorRow label="Border Color" value={settings.border_color} onChange={v => sset("border_color", v)} />
              </Row2>
              <div>
                <Lbl>Background Gradient (CSS)</Lbl>
                <Inp value={settings.bg_gradient} onChange={e => sset("bg_gradient", e.target.value)} placeholder="linear-gradient(90deg, #4F46E5, #7C3AED)" />
                <p className="text-[11px] text-gray-400 mt-1">Overrides background color when set.</p>
              </div>
              <div>
                <Lbl>Box Shadow (CSS)</Lbl>
                <Inp value={settings.shadow} onChange={e => sset("shadow", e.target.value)} placeholder="0 2px 8px rgba(0,0,0,0.2)" />
              </div>
            </div>
          </Card>

          <Card title="Typography">
            <Row2>
              <div>
                <Lbl>Font Family</Lbl>
                <Sel value={settings.font_family} onChange={e => sset("font_family", e.target.value)}>
                  {FONT_FAMILIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </Sel>
              </div>
              <div>
                <Lbl>Font Size (px)</Lbl>
                <Inp type="number" value={settings.font_size} onChange={e => sset("font_size", parseInt(e.target.value) || 14)} />
              </div>
              <div>
                <Lbl>Font Weight</Lbl>
                <Sel value={settings.font_weight} onChange={e => sset("font_weight", parseInt(e.target.value))}>
                  {[300, 400, 500, 600, 700].map(w => <option key={w} value={w}>{w}</option>)}
                </Sel>
              </div>
              <div>
                <Lbl>Font Style</Lbl>
                <Sel value={settings.font_style} onChange={e => sset("font_style", e.target.value)}>
                  <option value="normal">Normal</option>
                  <option value="italic">Italic</option>
                </Sel>
              </div>
              <div>
                <Lbl>Letter Spacing (px)</Lbl>
                <Inp type="number" step="0.5" value={settings.letter_spacing} onChange={e => sset("letter_spacing", parseFloat(e.target.value) || 0)} />
              </div>
            </Row2>
          </Card>

          <Card title="Scroll & Animation">
            <Row2>
              <div>
                <Lbl>Scroll Speed (px/s)</Lbl>
                <Inp type="number" value={settings.scroll_speed} onChange={e => sset("scroll_speed", parseInt(e.target.value) || 40)} />
              </div>
              <div>
                <Lbl>Direction</Lbl>
                <Sel value={settings.direction} onChange={e => sset("direction", e.target.value)}>
                  <option value="ltr">Left → Right</option>
                  <option value="rtl">Right → Left</option>
                </Sel>
              </div>
              <div>
                <Lbl>Gap Between Items (px)</Lbl>
                <Inp type="number" value={settings.gap} onChange={e => sset("gap", parseInt(e.target.value) || 60)} />
              </div>
              <div>
                <Lbl>Separator Character</Lbl>
                <Inp value={settings.separator} onChange={e => sset("separator", e.target.value)} placeholder="•" />
              </div>
            </Row2>
          </Card>

          <Card title="Glassmorphism">
            <Toggle label="Enable Glassmorphism" desc="Apply glass/blur effect to the ticker bar." checked={!!settings.glassmorphism} onChange={v => sset("glassmorphism", v)} />
            {settings.glassmorphism && (
              <Row2>
                <div><Lbl>Background Opacity (0–1)</Lbl><Inp type="number" step="0.05" min="0" max="1" value={settings.glass_opacity} onChange={e => sset("glass_opacity", parseFloat(e.target.value) || 0.15)} /></div>
                <div><Lbl>Blur Amount (px)</Lbl><Inp type="number" value={settings.glass_blur} onChange={e => sset("glass_blur", parseInt(e.target.value) || 8)} /></div>
              </Row2>
            )}
          </Card>

          <Card title="Live Preview">
            <p className="text-xs text-gray-400 mb-3">Updates as you change settings above.</p>
            <div className="space-y-3">
              {[{ label: "Desktop", w: "100%" }, { label: "Tablet", w: "640px" }, { label: "Mobile", w: "375px" }].map(({ label, w }) => (
                <div key={label}>
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    {label === "Desktop" && <Monitor className="w-3 h-3" />}
                    {label === "Tablet" && <Tablet className="w-3 h-3" />}
                    {label === "Mobile" && <Smartphone className="w-3 h-3" />}
                    {label}
                  </div>
                  <div style={{ maxWidth: w, overflow: "hidden", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                    <TickerPreview settings={settings} items={previewItems} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className="flex flex-wrap gap-3">
            <button onClick={saveSettings} disabled={savingSettings}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-[#0F172A] text-white text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-[#1e293b] transition disabled:opacity-60">
              <Save className="w-4 h-4" />{savingSettings ? "Saving…" : "Save Settings"}
            </button>
            <button onClick={load} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 border border-gray-200 bg-white text-gray-700 text-sm px-6 py-2.5 rounded-lg hover:bg-gray-50 transition">
              <RotateCcw className="w-4 h-4" />Reload
            </button>
          </div>
        </>)}

        {/* ── LIVE PREVIEW TAB ── */}
        {tab === "preview" && (
          <div className="space-y-5">
            <p className="text-xs text-gray-500">Live preview — reflects current settings and visible news items.</p>
            {[
              { label: "Desktop", w: "100%", Icon: Monitor },
              { label: "Tablet", w: "768px", Icon: Tablet },
              { label: "Mobile", w: "375px", Icon: Smartphone },
            ].map(({ label, w, Icon }) => (
              <div key={label}>
                <div className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5" />{label} ({w})
                </div>
                <div style={{ maxWidth: w, overflow: "hidden", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                  <TickerPreview settings={settings} items={previewItems} />
                </div>
              </div>
            ))}
            <div className="text-center pt-2">
              <button onClick={() => setTab("settings")} className="text-xs text-indigo-600 hover:underline">
                ← Go to Settings to adjust appearance
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

/* ── Shared News Item Row List ── */
function NewsItemList({ items, allItems, onToggle, onPin, onEdit, onDuplicate, onDelete, onMove, isBookingTab }) {
  return (
    <div className="divide-y divide-gray-100 -my-1">
      {items.map((item, idx) => {
        const Ico = ICON_MAP[item.icon] || Megaphone;
        const globalIdx = allItems.findIndex(n => n.id === item.id);
        return (
          <div key={item.id} className={`py-3 flex items-start gap-3 ${!item.visible ? "opacity-50" : ""}`}>
            {/* Reorder */}
            <div className="flex flex-col gap-0.5 shrink-0 mt-0.5">
              <button onClick={() => onMove(item, "up")} disabled={idx === 0} className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-20"><ChevronUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => onMove(item, "down")} disabled={idx === items.length - 1} className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-20"><ChevronDown className="w-3.5 h-3.5" /></button>
            </div>
            <div className="shrink-0 mt-1">
              <Ico style={{ width: 16, height: 16, color: item.color || "#4F46E5" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-800 truncate">{item.headline}</span>
                {item.pinned && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Pinned</span>}
                {item.source === "booking" && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">Auto</span>}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${item.visible ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                  {item.visible ? "Visible" : "Hidden"}
                </span>
              </div>
              {item.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{item.description}</p>}
              <p className="text-[10px] text-gray-400 mt-0.5">
                {item.news_type} · priority {item.priority} · order {item.display_order}
                {item.expiry_date && ` · expires ${item.expiry_date}`}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => onToggle(item)} title={item.visible ? "Hide" : "Show"}
                className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                {item.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => onPin(item)} title={item.pinned ? "Unpin" : "Pin"}
                className={`p-1.5 rounded hover:bg-gray-100 transition ${item.pinned ? "text-amber-500" : "text-gray-400 hover:text-gray-600"}`}>
                <Pin className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onEdit(item)} title="Edit"
                className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                <Settings2 className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onDuplicate(item)} title="Duplicate"
                className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onDelete(item)} title="Delete"
                className="p-1.5 rounded hover:bg-rose-50 text-gray-400 hover:text-rose-500">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
